import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import { SuiteState } from "../engine/engine";
import { DEFAULT_SUITE, suiteRepo } from "./persistence";

type SuiteStateContextValue = {
    value: SuiteState;
    set: (suite: SuiteState) => void;
    /**
     * `true` after the first storage read. Until then `value` is the
     * empty default, not actual storage. The Loading overlay reads
     * this to know when to fade.
     */
    loaded: boolean;
};

const SuiteStateContext = createContext<SuiteStateContextValue>({
    value: DEFAULT_SUITE,
    set: () => {},
    loaded: false,
});

/**
 * Production provider. Initial state is empty to match SSR, then a
 * `useEffect` reads localStorage and broadcasts. Cross-tab updates flow
 * through the same broadcast subscription. Consumers gate UX that
 * depends on actual contents on `loaded`.
 */
export const SuiteStateProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [value, setValue] = useState<SuiteState>(DEFAULT_SUITE);
    const [loaded, setLoaded] = useState(false);

    const set = useCallback((suite: SuiteState) => {
        suiteRepo
            .set(suite)
            .then(setValue)
            .catch((err: unknown) => console.error(err));
    }, []);

    useEffect(() => {
        const refresh = () => {
            suiteRepo
                .get()
                .then(setValue)
                .catch((err: unknown) => console.error(err))
                .finally(() => setLoaded(true));
        };
        refresh();
        suiteRepo.addDataChangeListener(refresh);
        return () => {
            suiteRepo.removeDataChangeListener(refresh);
        };
    }, []);

    return (
        <SuiteStateContext.Provider value={{ value, set, loaded }}>
            {children}
        </SuiteStateContext.Provider>
    );
};

/**
 * Storybook / test provider. Holds React state internally so stories
 * can interact normally; never reads from or writes to localStorage,
 * never imports the broadcast channel. `loaded` defaults to `true` so
 * stories don't sit in skeleton state.
 */
export const MockSuiteStateProvider = ({
    suite = DEFAULT_SUITE,
    children,
}: {
    suite?: SuiteState;
    children: React.ReactNode;
}) => {
    const [value, setValue] = useState<SuiteState>(suite);
    return (
        <SuiteStateContext.Provider
            value={{ value, set: setValue, loaded: true }}
        >
            {children}
        </SuiteStateContext.Provider>
    );
};

export const useSuiteState = (): SuiteStateContextValue =>
    useContext(SuiteStateContext);
